
import moment, { Moment } from 'moment';
import { mapState, mapStores } from 'pinia';
import { NTabPane, NTabs } from 'naive-ui';
import { defineComponent } from 'vue';
import { LocationQuery } from 'vue-router';

import BasePage from '~/core/base-page';
import DeviceDetailsInformation from './device-details-information/device-details-information.vue';
import DeviceDetailsHeader from './device-details-header/device-details-header.vue';

import DeviceDetailsNetwork from './device-details-network/device-details-network.vue';
import DeviceDetailsOverview from '~/pages/device-details/device-details-overview/device-details-overview';
import DeviceDetailsHistory from '~/pages/device-details/device-details-history/device-details-history';
import DeviceDetailsData from '~/pages/device-details/device-details-data/device-details-data.vue';
import DeviceDetailsConfiguration from '~/pages/device-details/device-details-configuration/device-details-configuration.vue';

import { StoreError } from "~/exceptions/store.error";
import { useStationDetailStore } from '~/store/station-details.store';
import { useStationStore } from '~/store/station.store';
import DeviceDetailsEvents from './device-details-events/device-details-events.vue';



export default defineComponent({

    components: {
        DeviceDetailsHeader,
        DeviceDetailsInformation,

        DeviceDetailsOverview,
        DeviceDetailsNetwork,
        DeviceDetailsHistory,
        DeviceDetailsData,
        DeviceDetailsEvents,
        DeviceDetailsConfiguration,

        NTabs,
        NTabPane,
    },

    extends: BasePage,

    data() {
        return {
            loading: false,
            fullscreen: false,
        }
    },

    computed: {
        ...mapStores(useStationStore, useStationDetailStore),
        ...mapState(useStationStore, ['group']),
        ...mapState(useStationDetailStore, ['station', 'events', 'measures']),
    },

    methods: {

        async refresh(): Promise<void> {
            await this.stationDetailsStore.refresh();
        },

        /**
         * Handle document visibilitychange event. Refresh dashboard if tab is active.
         */
         handleVisibilityChange(): void {
            if(document.visibilityState === 'visible') {
                try {
                    this.refresh();
                } catch(error) {
                    this.showUnhandledError('Could not refresh', error);
                }
            }
        },
    },

    async mounted(): Promise<void> {

        const group: string = this.$route.params?.group as string;
        const station: string = this.$route?.params?.station as string;

        // init group
        if(!group || !station) {
            console.error(`Missing params group or station. Current params are ${group} and ${station}`);
            this.$router.replace({ name: 'not-found', });
            return;
        }

        
        try {

            await this.stationStore.init();

            if(!this.stationStore.groups?.length) {
                this.$router.push({name: 'wrong-configuration'});
                return;
            }

        } catch (error) {
            if(error instanceof StoreError ) {
                this.$router.push({ name: 'wrong-configuration' });
                return;
            } else {
                throw error;
            }
        }
        
        const since = this.$route.query.since as string;
        const before = this.$route.query.before as string;
        
        try {
            // select group if it is not loaded yet
            if(this.group?.name !== group) {
                await this.stationStore.selectStationGroup(group);
            }

            // select station
            await this.stationDetailsStore.setCurrentStation(station);

            // set timerange
            if(since && before) {
                this.stationDetailsStore.setTimerange(moment(since), moment(before));
            } else {
                this.stationDetailsStore.setTimerange(moment().startOf('days').subtract(1, 'months'), moment().endOf('days'))
            }

            await this.stationDetailsStore.refresh();
            this.stationDetailsStore.initSocket();

        } catch(error) {
            if (error instanceof StoreError) {
                console.error('Could not page', error);
                // this.$router.replace({ name: 'not-found' });
                return;
            } else {
                console.error('An error occured while initializing page', error);
                throw error
            }
        }
    },

    watch: {
        async $route(to: { query: LocationQuery, path: string }, from: { query:LocationQuery, path: string }) {

            if (!to.path.includes('station')) {
                return;
            }

            let refresh: boolean = false;

            try {
                if(this.group?.name !== this.$route?.params.group) {
                    await this.stationStore.selectStationGroup(this.$route?.params.group as string);
                    await this.stationStore.refresh();
                    refresh = true;
                }
                

                if(this.station?.id !== this.$route.params?.station){
                    await this.stationDetailsStore.setCurrentStation(this.$route.params?.station as string);
                    refresh = true;
                }
            } catch(error) {
                if (error instanceof StoreError) {
                    this.$router.push({ name: 'not-found' });
                    return;
                } else {
                    throw error;
                }
            }

            let since: Moment = null;
            if(to?.query?.since && to.query?.since !== from?.query?.since) {
                since = moment(to?.query?.since);
            }

            let before: Moment = null;
            if(to?.query?.before && to.query?.before !== from?.query?.before) {
                before = moment(to?.query?.before);
            }

            if(since && before) {
                await this.stationDetailsStore.setTimerange(since, before);
                refresh = true;
            }

            if(refresh) {

                try {
                    await this.stationDetailsStore.refresh();
                    this.stationDetailsStore.initSocket();
                } catch(error) {
                    this.stationDetailsStore?.$reset();
                    console.error(error);
                }

            }

        },
    }
})